import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import { Question } from 'models/Question'
import DatePickerField from 'modules/ui/molecules/FormComponents/DatePickerField'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import TextAreaField from 'modules/ui/molecules/FormComponents/TextAreaField'
import RadioGroupField from 'modules/ui/molecules/FormComponents/RadioGroupField'
import NumberInputField from 'modules/ui/molecules/FormComponents/NumberInputField'
import DollarInputField from 'modules/ui/molecules/FormComponents/DollarInputField'
import WaiverField from 'modules/ui/molecules/FormComponents/WaiverField'
import Label from 'modules/ui/atoms/Typography/Label'
import { DEFAULT_FONT_STYLES } from 'css/theme'
import styled from '@emotion/styled'
import { CLASSY_DESIGN_TOKENS } from '../../../../tokens'
import useConfig from 'modules/config/state/use-config'

import mParticleSdk from '@mparticle/web-sdk'
import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'

const TextAreaLabel = styled.div`
  margin: 12px 0px 0px;
  font-size: 14px;
  line-height: 20px;
  color: ${CLASSY_DESIGN_TOKENS['--classy-color__neutral--30']};
`

const EMBEDDED_LABEL_CONFIG = {
  string: { key: 'custom-question-field.string.label', fallback: 'Your answer' },
  text: { key: 'custom-question-field.string.label', fallback: 'Your answer' },
  integer: { key: 'custom-question-field.integer.label', fallback: 'Enter a number' },
  currency: { key: 'custom-question-field.currency.label', fallback: 'Enter a dollar amount' },
  enum: { key: 'custom-question-field.enum.selectOption', fallback: 'Select an option' },
  date: { key: 'custom-question-field.date.label', fallback: 'Select a date' },
  yes_no: { key: '', fallback: '' },
  boolean: { key: '', fallback: '' },
} as const

interface OrderCustomQuestionFieldProps {
  question: Question
}

const getFieldLabel = (
  questionType: Question['type'],
  questionLabel: string,
  isEmbedded: boolean,
  t: (key: string, fallback: string) => string,
): string => {
  if (!isEmbedded) {
    return questionLabel
  }

  const config = EMBEDDED_LABEL_CONFIG[questionType]

  if (!config || !config.key) {
    return ''
  }

  return t(config.key, config.fallback)
}

const getMainQuestionLabel = (
  question: Question,
  t: (key: string, fallback: string) => string,
): string => {
  return question.type === 'boolean'
    ? t('custom-question-field.boolean.acceptTerms', 'By clicking accept you agree to these terms.')
    : question.label
}

export const OrderCustomQuestionField = ({ question }: OrderCustomQuestionFieldProps) => {
  const form = useFormContext()
  const { t } = useTranslation()
  const config = useConfig()

  const trackUIElementEvent = useTrackUIElementEvent()

  const isEmbedded = config.isEmbedded
  const fieldError = form?.formState.errors[question.id] as { message: string } | undefined
  let fieldEl = null

  const fieldDataId = `custom-question-${question.id}`

  const handleCustomQuestionFocus = () => {
    trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
      tracking_id: 'custom question start',
      value: question.label,
    })
  }

  switch (question.type) {
    case 'string': {
      const label = getFieldLabel('string', question.label, isEmbedded, t)

      fieldEl = (
        <InputField
          id={question.id}
          error={fieldError}
          label={label}
          maxLength={255}
          hasCounter
          dataId={fieldDataId}
          required={question.is_required}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break
    }

    case 'text': {
      const embeddedLabel = getFieldLabel('text', question.label, isEmbedded, t)

      fieldEl = (
        <>
          {isEmbedded && <TextAreaLabel>{embeddedLabel}</TextAreaLabel>}
          <TextAreaField
            id={question.id}
            error={fieldError}
            label={isEmbedded ? '' : question.label}
            required={question.is_required}
            maxLength={255}
            hasCounter
            dataId={fieldDataId}
            onFocus={handleCustomQuestionFocus}
          />
        </>
      )

      break
    }

    case 'yes_no': {
      const label = getFieldLabel('yes_no', question.label, isEmbedded, t)

      fieldEl = (
        <RadioGroupField
          id={question.id}
          error={fieldError}
          label={label}
          required={question.is_required}
          options={[
            { key: 'yes', label: t('custom-question-field.yes_no.yes', 'Yes') },
            { key: 'no', label: t('custom-question-field.yes_no.no', 'No') },
          ]}
          dataId={fieldDataId}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break
    }

    case 'integer': {
      const label = getFieldLabel('integer', question.label, isEmbedded, t)

      fieldEl = (
        <NumberInputField
          id={question.id}
          error={fieldError}
          label={label}
          required={question.is_required}
          dataId={fieldDataId}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break
    }

    case 'currency': {
      const label = getFieldLabel('currency', question.label, isEmbedded, t)

      fieldEl = (
        <DollarInputField
          id={question.id}
          error={fieldError}
          label={label}
          required={question.is_required}
          dataId={fieldDataId}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break
    }

    case 'enum': {
      const EMPTY_OPTION = { label: '', value: '' }
      const label = getFieldLabel('enum', question.label, isEmbedded, t)

      const options = question.enum_options
        ? [
            EMPTY_OPTION,
            ...question.enum_options.map((option) => ({
              label: option.label,
              value: option.label,
            })),
          ]
        : []

      fieldEl = (
        <SelectField
          id={question.id}
          error={fieldError}
          label={label}
          placeholder={t('custom-question-field.enum.chooseOne', 'Choose one...')}
          required={question.is_required}
          options={options}
          dataId={fieldDataId}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break
    }

    case 'date': {
      const label = getFieldLabel('date', question.label, isEmbedded, t)

      fieldEl = (
        <DatePickerField
          id={question.id}
          error={fieldError}
          label={label}
          required={question.is_required}
          dataId={fieldDataId}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break
    }

    case 'boolean': // Waiver question type
      fieldEl = (
        <WaiverField
          id={question.id}
          error={fieldError}
          label={question.label}
          required={question.is_required}
          dataId={fieldDataId}
          onFocus={handleCustomQuestionFocus}
        />
      )

      break

    default:
      break
  }

  const labelText = getMainQuestionLabel(question, t)

  return (
    <div>
      {isEmbedded && (
        <Label
          style={{
            marginBottom: 16,
            textAlign: isEmbedded ? 'center' : 'left',
            justifyContent: isEmbedded ? 'center' : 'flex-start',
            lineHeight: isEmbedded
              ? DEFAULT_FONT_STYLES['--theme-font--title-page__line-height']
              : DEFAULT_FONT_STYLES['--theme-font--body-default__line-height'],
            fontSize: isEmbedded
              ? DEFAULT_FONT_STYLES['--theme-font--title-page__font-size']
              : DEFAULT_FONT_STYLES['--theme-font--body-default__font-size'],
            fontWeight: DEFAULT_FONT_STYLES['--theme-font--body-bold__font-weight'],
          }}
        >
          {labelText}
        </Label>
      )}
      {fieldEl}
    </div>
  )
}

export default OrderCustomQuestionField
